import { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import UserAvatar from '../userAvatar';
import ActionButton from '../actionButton';
import ShareTypeBox from '../shareTypeBox';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';
import ToggleComponent from '../toggleComponent';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  userAvatar: {
    width: 'calc(100% - 24px)',
  },
  avatarLabel: {
    color: primary.bluish2,
    fontSize: 16,
    lineHeight: '20px',
    marginLeft: 0,
    paddingRight: spacing(1.5),
    width: '100%',
    userSelect: 'none',
  },
  avatarLabelSelected: {
    color: primary.bluish1,
    fontFamily: 'ProximaNova-Bold',
    fontSize: 14,
    lineHeight: '18px',
  },
  box: {
    minWidth: 'fit-content',
  },
  closeIconButton: {
    height: 24,
    width: 24,
  },
  closeIcon: {
    height: 16,
    width: 16,
    '& path': {
      fill: primary.bluish1,
    },
  },
  closeIconDisabled: {
    '& path': {
      fill: primary.bluish5,
    },
  },
  disabledAvatar: {
    color: primary.bluish5,
  },
}));

const VirtualSelectedUsers = ({
  translations = {},
  item,
  typeLabel = '',
  shouldHandleTypeLabel = false,
  isRemoved = false,
  hasDelete = false,
  onRevert = () => {},
  onDeselect = () => {},
}) => {
  const classes = useStyles();

  const handleDeselect = useCallback(
    () => onDeselect(item),
    [item, onDeselect]
  );

  const handleRevert = useCallback(() => onRevert(item), [item, onRevert]);

  return (
    <div className={classes.root}>
      <UserAvatar
        className={classes.userAvatar}
        labelClass={classNames(
          classes.avatarLabel,
          classes.avatarLabelSelected,
          { [classes.disabledAvatar]: isRemoved }
        )}
        user={item}
        caption
        small
      />
      {shouldHandleTypeLabel && typeLabel && (
        <ShareTypeBox className={classes.box} label={typeLabel} />
      )}
      {hasDelete && (
        <ToggleComponent inKey={!isRemoved}>
          {!isRemoved ? (
            <ActionButton
              className={classes.closeIconButton}
              iconClass={classNames(classes.closeIcon, {
                [classes.closeIconDisabled]: item.isDisabled,
              })}
              type={ACTION_BUTTON_TYPES.CLOSE}
              disabledTooltipText={translations?.disabledUserTooltip}
              isDisabled={item.isDisabled}
              onClickHandler={handleDeselect}
            />
          ) : (
            <ActionButton
              className={classes.closeIconButton}
              iconClass={classes.closeIcon}
              type={ACTION_BUTTON_TYPES.RELOAD}
              onClickHandler={handleRevert}
            />
          )}
        </ToggleComponent>
      )}
    </div>
  );
};

VirtualSelectedUsers.propTypes = {
  translations: PropTypes.object,
  typeLabel: PropTypes.string,
  item: PropTypes.object.isRequired,
  shouldHandleTypeLabel: PropTypes.bool,
  isRemoved: PropTypes.bool,
  hasDelete: PropTypes.bool,
  onRevert: PropTypes.func,
  onDeselect: PropTypes.func,
};

export default memo(VirtualSelectedUsers);
